/**
 * SpecialEventsPage - Curated speed dating events schedule
 * 
 * Shows upcoming special events with:
 * - Event cards (name, type, time, eligibility)
 * - Ticketed badge where applicable
 * - CTAs based on user state
 */

import React, { useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { trackEvent } from '../api/analytics';
import { Calendar, Users, Clock, Ticket, Shield, ArrowLeft } from 'lucide-react';
import InlineNotice from '../components/common/InlineNotice';

// Event types with their themes
const EVENT_TYPES = {
  'the-circle': {
    name: 'The Circle',
    description: 'Intimate rounds with deeper conversation prompts',
    color: 'from-purple-500 to-indigo-500',
    icon: '⭕',
  },
  'after-hours': {
    name: 'After Hours',
    description: 'Late night connections for night owls',
    color: 'from-blue-500 to-purple-500',
    icon: '🌙',
  },
  'poly-paths': {
    name: 'Poly Paths',
    description: 'For those exploring ethical non-monogamy',
    color: 'from-pink-500 to-rose-500',
    icon: '💜',
  },
  'shared-ground': {
    name: 'Shared Ground',
    description: 'Values-based matching for deeper compatibility',
    color: 'from-emerald-500 to-teal-500',
    icon: '🌱',
  },
  'diaspora-night': {
    name: 'Diaspora Night',
    description: 'Cultural connections and shared heritage',
    color: 'from-amber-500 to-orange-500',
    icon: '🌍',
  },
};

// Placeholder upcoming events
const UPCOMING_EVENTS = [
  {
    id: 'evt-1',
    type: 'the-circle',
    name: 'The Circle: First Fridays',
    date: 'Friday, Jan 10',
    time: '8:00 PM EST',
    spots: 20,
    ticketed: true,
    price: '$15',
    eligibility: 'verified',
    status: 'upcoming',
  },
  {
    id: 'evt-2',
    type: 'after-hours',
    name: 'After Hours: Insomniacs Edition',
    date: 'Saturday, Jan 11',
    time: '11:00 PM EST',
    spots: 16,
    ticketed: false,
    eligibility: 'inner-circle',
    status: 'upcoming',
  },
  {
    id: 'evt-3',
    type: 'poly-paths',
    name: 'Poly Paths: New Connections',
    date: 'Sunday, Jan 12',
    time: '6:00 PM EST',
    spots: 24,
    ticketed: true,
    price: '$10',
    eligibility: 'verified',
    status: 'upcoming',
  },
  {
    id: 'evt-4',
    type: 'diaspora-night',
    name: 'Diaspora Night: Caribbean + US',
    date: 'Friday, Jan 17',
    time: '7:30 PM EST',
    spots: 30,
    ticketed: true,
    price: '$12',
    eligibility: 'verified',
    status: 'coming-soon',
  },
];

export default function SpecialEventsPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Derive user state
  const isVerified = user?.identity_verified || user?.identity?.status === 'verified';
  const isInnerCircle = user?.entitlements?.inner_circle?.active;

  // Track page view on mount
  React.useEffect(() => {
    trackEvent('special_events_viewed', { 
      is_verified: isVerified,
      is_inner_circle: isInnerCircle
    });
  }, [isVerified, isInnerCircle]);

  const handleEventAction = useCallback((event) => {
    trackEvent('ticket_cta_clicked', {
      event_id: event.id,
      event_type: event.type,
      event_name: event.name,
      eligibility: event.eligibility,
      ticketed: event.ticketed,
      price: event.price || null,
      user_is_verified: isVerified,
      user_is_inner_circle: isInnerCircle,
    });

    // Check eligibility
    if (event.eligibility === 'inner-circle' && !isInnerCircle) {
      navigate('/shop?plan=inner-circle');
      return;
    }

    if (!isVerified) {
      navigate(`/verify-age?return=/events/special`);
      return;
    }

    // For now, show coming soon
    alert('Tickets coming soon! You\'ll be notified when this event opens.');
  }, [isVerified, isInnerCircle, navigate]);

  const getEligibilityBadge = (eligibility) => {
    if (eligibility === 'inner-circle') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-300 border border-purple-500/30">
          <Shield className="w-3 h-3" />
          Inner Circle
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
        <Shield className="w-3 h-3" />
        Verified
      </span>
    );
  };

  const getActionButton = (event) => {
    // Inner Circle only event
    if (event.eligibility === 'inner-circle' && !isInnerCircle) {
      return (
        <button
          onClick={() => handleEventAction(event)}
          className="w-full py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-purple-500/80 to-pink-500/80 text-white hover:brightness-110 transition"
        >
          Join Inner Circle
        </button>
      );
    }

    // Not verified
    if (!isVerified) {
      return (
        <button
          onClick={() => handleEventAction(event)}
          className="w-full py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-amber-500/80 to-orange-500/80 text-white hover:brightness-110 transition"
        >
          Verify to Join
        </button>
      );
    }

    // Verified user - can get ticket
    if (event.ticketed) {
      return (
        <button
          onClick={() => handleEventAction(event)}
          className="w-full py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:brightness-110 transition"
        >
          Get Ticket {event.price && `• ${event.price}`}
        </button>
      );
    }

    // Free event
    return (
      <button
        onClick={() => handleEventAction(event)}
        className="w-full py-2.5 rounded-xl text-sm font-medium border border-white/20 text-white/80 hover:bg-white/5 transition"
      >
        Reserve Spot
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 px-4 py-6 pb-24">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl bg-white/5 hover:bg-white/10 transition"
          >
            <ArrowLeft className="w-5 h-5 text-white/70" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">Special Events</h1>
            <p className="text-sm text-white/60">Curated experiences, limited spots</p>
          </div>
        </div>

        {/* Intro */}
        <div className="mb-6 p-4 rounded-2xl bg-white/5 border border-white/10">
          <p className="text-sm text-white/70">
            Special events offer extended rounds, themed nights, and curated matching. 
            All events are video speed dating with verified members only.
          </p>
        </div>

        {/* Verification Notice */}
        {!isVerified && (
          <div className="mb-4">
            <InlineNotice
              variant="warning"
              title="Verified access"
              actionLabel="Verify now"
              href={`/verify-age?return=${encodeURIComponent(window.location.pathname)}`}
              testId="special-events-verify-notice"
            >
              Some events are capped and curated. Verification helps keep things real and respectful.
            </InlineNotice>
          </div>
        )}

        {/* Event Cards */}
        <div className="space-y-4">
          {UPCOMING_EVENTS.map((event) => {
            const typeInfo = EVENT_TYPES[event.type];
            
            return (
              <div
                key={event.id}
                className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden"
                data-testid={`special-event-card-${event.id}`}
              >
                {/* Event Type Header */}
                <div className={`px-4 py-2 bg-gradient-to-r ${typeInfo.color} bg-opacity-20`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{typeInfo.icon}</span>
                    <span className="text-sm font-medium text-white/90">{typeInfo.name}</span>
                    {event.ticketed && (
                      <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-black/30 text-white/80">
                        <Ticket className="w-3 h-3" />
                        Ticketed
                      </span>
                    )}
                  </div>
                </div>

                {/* Event Details */}
                <div className="p-4">
                  <h3 className="text-base font-semibold text-white/90">{event.name}</h3>
                  <p className="mt-1 text-xs text-white/50">{typeInfo.description}</p>

                  {/* Meta */}
                  <div className="mt-3 flex flex-wrap gap-3 text-xs text-white/60">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {event.date}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {event.time}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {event.spots} spots
                    </span>
                  </div>

                  {/* Eligibility Badge */}
                  <div className="mt-3">
                    {getEligibilityBadge(event.eligibility)}
                  </div>

                  {/* Action Button */}
                  <div className="mt-4">
                    {event.status === 'coming-soon' ? (
                      <button
                        disabled
                        className="w-full py-2.5 rounded-xl text-sm font-medium bg-white/5 text-white/40 cursor-not-allowed"
                      >
                        Coming Soon
                      </button>
                    ) : (
                      getActionButton(event)
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Note */}
        <p className="mt-6 text-xs text-white/30 text-center">
          All events are video-based • Private • No recording • Verified members only
        </p>
      </div>
    </div>
  );
}
